import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../utils/api';
import {
  ArrowLeft, FileDown, Edit2, Check, X, Save, Trash2,
  Plus, ChevronDown, ChevronUp, Package, Send,
} from 'lucide-react';
import toast from 'react-hot-toast';
const getToken = () => localStorage.getItem('token') || sessionStorage.getItem('token') || null;


const STATUS_OPTS  = ['offen','in_bearbeitung','erledigt'];
const STATUS_LABEL = { offen:'Offen', in_bearbeitung:'In Bearbeitung', erledigt:'Erledigt' };
const STATUS_COLOR = { offen:'var(--red)', in_bearbeitung:'var(--yellow)', erledigt:'var(--green)' };
const STATUS_BG    = { offen:'var(--red-bg)', in_bearbeitung:'var(--yellow-bg)', erledigt:'var(--green-bg)' };

const CATEGORIES = [
  'Getränke-Equipment','Geschirr','Zelt & Mobiliar','Elektro',
  'Reinigung','Verbrauchsmaterial','Sonstiges','Allgemein',
];

const UNITS = ['Stück','Liter','Kilo','Rollen','Packungen','Set','Check','Paar'];

// Leere Item-Form
const EMPTY_ITEM = { name:'', quantity:1, unit:'Stück', category:'Sonstiges', note:'' };

export default function PacklisteDetail() {
  const { id } = useParams();
  const nav    = useNavigate();

  const [list, setList]           = useState(null);
  const [items, setItems]         = useState({});       // { Kategorie: [item, ...] }
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [editingList, setEditList]= useState(false);
  const [form, setForm]           = useState({});
  const [saving, setSaving]       = useState(false);
  const [collapsed, setCollapsed] = useState({});

  // Item hinzufügen
  const [addModal, setAddModal]   = useState(false);
  const [newItem, setNewItem]     = useState(EMPTY_ITEM);
  const [addingSave, setAddingSave] = useState(false);

  // Item bearbeiten
  const [editItem, setEditItem]   = useState(null); // item object
  const [editItemForm, setEditItemForm] = useState({});
  const [sendingEmail, setSendingEmail] = useState(false);

  // Packliste laden
  const load = useCallback(async () => {
    if (!id) { setError('Keine ID angegeben'); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/packing/${id}`);
      const data = res.data;
      setList(data);
      setForm({
        title:      data.title      || '',
        event_date: data.event_date || '',
        location:   data.location   || '',
        notes:      data.notes      || '',
      });

      // Items aus der Liste (Backend gibt sie direkt mit zurück)
      const rawItems = Array.isArray(data.items) ? data.items : [];

      // Wenn keine Items direkt dabei, separat laden
      if (rawItems.length === 0) {
        try {
          const itemsRes = await api.get(`/packing/${id}/items`);
          buildItemGroups(Array.isArray(itemsRes.data) ? itemsRes.data : []);
        } catch {
          buildItemGroups([]);
        }
      } else {
        buildItemGroups(rawItems);
      }
    } catch(err) {
      const msg = err.response?.data?.error || err.message || 'Unbekannter Fehler';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [id]);

  function buildItemGroups(rawItems) {
    const grouped = {};
    rawItems.forEach(item => {
      const cat = item.category || 'Sonstiges';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(item);
    });
    setItems(grouped);
  }

  useEffect(() => { load(); }, [load]);

  // Packliste speichern
  const saveList = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/packing/${id}`, form);
      toast.success('Gespeichert');
      setEditList(false);
      setList(prev => ({ ...prev, ...form }));
    } catch(err) {
      toast.error(err.response?.data?.error || 'Fehler');
    } finally { setSaving(false); }
  };

  // Status setzen
  const setStatus = async (status) => {
    try {
      await api.put(`/packing/${id}`, { status });
      setList(prev => ({ ...prev, status }));
      toast.success(`Status: ${STATUS_LABEL[status]}`);
    } catch { toast.error('Fehler'); }
  };

  // Item abhaken
  const toggleItem = async (item) => {
    const newPacked = !item.packed;
    // Optimistisch updaten
    setItems(prev => {
      const next = {};
      Object.keys(prev).forEach(cat => {
        next[cat] = prev[cat].map(it => it.id === item.id ? { ...it, packed: newPacked } : it);
      });
      return next;
    });
    try {
      await api.put(`/packing/${id}/items/${item.id}`, {
        packed: newPacked,
        status: newPacked ? 'gepackt' : 'offen',
      });
    } catch {
      // Rollback
      setItems(prev => {
        const next = {};
        Object.keys(prev).forEach(cat => {
          next[cat] = prev[cat].map(it => it.id === item.id ? { ...it, packed: !newPacked } : it);
        });
        return next;
      });
      toast.error('Fehler');
    }
  };

  // Artikel hinzufügen
  const addItem = async () => {
    if (!newItem.name.trim()) { toast.error('Name ist Pflicht'); return; }
    setAddingSave(true);
    try {
      const res = await api.post(`/packing/${id}/items`, {
        name:     newItem.name.trim(),
        quantity: parseFloat(newItem.quantity) || 1,
        unit:     newItem.unit || 'Stück',
        category: newItem.category || 'Sonstiges',
        note:     newItem.note || '',
      });
      // Direkt in State hinzufügen
      const item = res.data;
      const cat  = item.category || 'Sonstiges';
      setItems(prev => ({
        ...prev,
        [cat]: [...(prev[cat] || []), item],
      }));
      toast.success('Artikel hinzugefügt');
      setNewItem(EMPTY_ITEM);
      setAddModal(false);
    } catch(err) {
      toast.error(err.response?.data?.error || 'Fehler beim Hinzufügen');
    } finally { setAddingSave(false); }
  };

  // Artikel löschen
  const deleteItem = async (item, e) => {
    e.stopPropagation();
    if (!window.confirm(`"${item.name}" löschen?`)) return;
    try {
      await api.delete(`/packing/${id}/items/${item.id}`);
      setItems(prev => {
        const next = {};
        Object.keys(prev).forEach(cat => {
          const filtered = prev[cat].filter(it => it.id !== item.id);
          if (filtered.length > 0) next[cat] = filtered;
        });
        return next;
      });
      toast.success('Artikel gelöscht');
    } catch { toast.error('Fehler'); }
  };

  // Artikel bearbeiten speichern
  const saveEditItem = async () => {
    if (!editItemForm.name?.trim()) { toast.error('Name ist Pflicht'); return; }
    try {
      const res = await api.put(`/packing/${id}/items/${editItem.id}`, editItemForm);
      const updated = res.data;
      setItems(prev => {
        const next = {};
        Object.keys(prev).forEach(cat => {
          next[cat] = prev[cat].map(it => it.id === updated.id ? updated : it);
        });
        return next;
      });
      setEditItem(null);
      toast.success('Gespeichert');
    } catch { toast.error('Fehler'); }
  };

  // Packliste löschen
  const deleteList = async () => {
    if (!window.confirm(`"${list?.title}" wirklich löschen?`)) return;
    try {
      await api.delete(`/packing/${id}`);
      toast.success('Packliste gelöscht');
      nav('/packliste');
    } catch(err) {
      toast.error(err.response?.data?.error || 'Fehler');
    }
  };

  // Manuelle E-Mail senden
  const sendEmail = async () => {
    setSendingEmail(true);
    try {
      const res = await api.post(`/email/send-packing/${id}`, {
        message: `Status-Update: ${STATUS_LABEL[list?.status] || list?.status}`,
      });
      toast.success(`E-Mail an ${res.data.sent} Empfänger gesendet`);
    } catch(err) {
      const msg = err.response?.data?.error || 'Fehler beim Senden';
      if (msg.includes('Keine')) {
        toast.error('Keine E-Mail-Empfänger konfiguriert — unter E-Mail im Menü eintragen');
      } else {
        toast.error(msg);
      }
    } finally { setSendingEmail(false); }
  };

  const downloadPDF = () => {
    const token = getToken();
    window.open(`/api/packing/${id}/pdf?token=${token}`, '_blank');
  };

  // Loading
  if (loading) return (
    <Layout title="Packliste">
      <div className="loading-box"><div className="spinner spinner-lg"/><span>Wird geladen...</span></div>
    </Layout>
  );

  // Fehler
  if (error) return (
    <Layout title="Fehler">
      <button className="btn btn-ghost btn-sm" style={{ marginBottom:16, gap:6, paddingLeft:4 }} onClick={() => nav('/packliste')}>
        <ArrowLeft size={16}/> Zurück
      </button>
      <div className="card" style={{ padding:'24px 20px', textAlign:'center' }}>
        <div style={{ fontSize:'2rem', marginBottom:10 }}>❌</div>
        <div style={{ fontWeight:700, color:'var(--red)', marginBottom:8 }}>Fehler beim Laden</div>
        <div style={{ fontSize:'.82rem', color:'var(--text-muted)', marginBottom:16 }}>{error}</div>
        <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
          <button className="btn btn-primary" onClick={load}>Erneut versuchen</button>
          <button className="btn btn-secondary" onClick={() => nav('/packliste')}>Zur Übersicht</button>
        </div>
      </div>
    </Layout>
  );

  if (!list) return (
    <Layout title="Packliste">
      <button className="btn btn-ghost btn-sm" style={{ marginBottom:16, gap:6, paddingLeft:4 }} onClick={() => nav('/packliste')}>
        <ArrowLeft size={16}/> Zurück
      </button>
      <div className="card"><div className="empty-box"><h3>Nicht gefunden</h3></div></div>
    </Layout>
  );

  const allItems = Object.values(items).flat();
  const packed   = allItems.filter(i => i.packed || i.status === 'gepackt').length;
  const total    = allItems.length;
  const pct      = total > 0 ? Math.round(packed / total * 100) : 0;
  const pctColor = pct === 100 ? 'var(--green)' : pct > 50 ? 'var(--yellow)' : 'var(--accent)';

  const emoji = {
    schuetzenfest:'🎯', veranstaltung:'🎉', toilettenanhaenger:'🚽',
    kuehlanhaenger:'❄️', ausschankwagen:'🍺',
  }[list.event_type] || '📦';

  return (
    <Layout title="Packliste">

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, gap:10 }}>
        <button className="btn btn-ghost btn-sm" style={{ gap:6, paddingLeft:4 }} onClick={() => nav('/packliste')}>
          <ArrowLeft size={16}/> Zurück
        </button>
        <div style={{ display:'flex', gap:6 }}>
          <button className="btn btn-secondary btn-sm" onClick={downloadPDF}><FileDown size={13}/> PDF</button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={sendEmail}
            disabled={sendingEmail}
            title="E-Mail an alle Empfänger senden"
            style={{gap:5}}>
            {sendingEmail ? <span className="spinner" style={{width:12,height:12,borderWidth:2}}/> : <Send size={13}/>}
            {sendingEmail ? 'Sende...' : 'E-Mail'}
          </button>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setEditList(!editingList)}><Edit2 size={14}/></button>
          <button className="btn btn-danger btn-sm" style={{ padding:'6px 10px' }} onClick={deleteList}><Trash2 size={14}/></button>
        </div>
      </div>

      {/* Info Card */}
      <div className="card" style={{ marginBottom:14 }}>
        <div style={{ padding:'16px 18px' }}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:14 }}>
            <span style={{ fontSize:'1.6rem', flexShrink:0 }}>{emoji}</span>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:800, fontSize:'1.1rem' }}>{list.title}</div>
              <div style={{ fontSize:'.75rem', color:'var(--text-muted)', marginTop:3 }}>
                {list.event_type}
                {list.event_date && ` · ${new Date(list.event_date).toLocaleDateString('de-DE',{day:'2-digit',month:'long',year:'numeric'})}`}
                {list.location && ` · ${list.location}`}
              </div>
            </div>
          </div>

          {/* Status Buttons */}
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:'.68rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:8 }}>Status</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {STATUS_OPTS.map(s => (
                <button key={s} onClick={() => setStatus(s)} style={{
                  padding:'7px 14px', borderRadius:8, border:'1.5px solid',
                  fontFamily:'inherit', fontSize:'.82rem', fontWeight:700, cursor:'pointer',
                  display:'flex', alignItems:'center', gap:5, transition:'all 150ms',
                  background: list.status===s ? STATUS_BG[s] : 'transparent',
                  borderColor: list.status===s ? STATUS_COLOR[s] : 'var(--border)',
                  color: list.status===s ? STATUS_COLOR[s] : 'var(--text-muted)',
                }}>
                  {list.status===s && <Check size={13}/>}
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          {/* Fortschritt */}
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:'.78rem', color:'var(--text-muted)', marginBottom:5 }}>
              <span><strong style={{color:'var(--text)'}}>{packed}</strong> von <strong style={{color:'var(--text)'}}>{total}</strong> gepackt</span>
              <span style={{ fontWeight:700, color:pctColor }}>{pct}%</span>
            </div>
            <div style={{ height:8, background:'var(--border)', borderRadius:999, overflow:'hidden' }}>
              <div style={{ height:'100%', background:pctColor, borderRadius:999, width:`${pct}%`, transition:'width 400ms' }}/>
            </div>
          </div>
        </div>

        {/* Bearbeitungsformular */}
        {editingList && (
          <div style={{ borderTop:'1px solid var(--border)', padding:'16px 18px', background:'var(--bg)' }}>
            <form onSubmit={saveList} style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="f-group" style={{ gridColumn:'1/-1' }}>
                  <label className="f-label">Titel</label>
                  <input className="f-input" value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} required/>
                </div>
                <div className="f-group">
                  <label className="f-label">Datum</label>
                  <input type="date" className="f-input" value={form.event_date} onChange={e=>setForm(p=>({...p,event_date:e.target.value}))}/>
                </div>
                <div className="f-group">
                  <label className="f-label">Ort</label>
                  <input className="f-input" value={form.location} onChange={e=>setForm(p=>({...p,location:e.target.value}))}/>
                </div>
                <div className="f-group" style={{ gridColumn:'1/-1' }}>
                  <label className="f-label">Notizen</label>
                  <textarea className="f-input" value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={2}/>
                </div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving?<><span className="spinner" style={{width:13,height:13}}/>Speichern...</>:<><Save size={13}/>Speichern</>}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={()=>setEditList(false)}>
                  <X size={13}/> Abbrechen
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Artikel hinzufügen Button */}
      <button
        className="btn btn-secondary"
        style={{ width:'100%', justifyContent:'center', marginBottom:14, padding:'11px' }}
        onClick={() => { setNewItem(EMPTY_ITEM); setAddModal(true); }}>
        <Plus size={15}/> Artikel hinzufügen
      </button>

      {/* Artikel-Modal */}
      {addModal && (
        <div
          style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(0,0,0,.5)', backdropFilter:'blur(4px)', WebkitBackdropFilter:'blur(4px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}
          onClick={e => e.target===e.currentTarget && setAddModal(false)}>
          <div style={{ background:'var(--bg-card)', borderRadius:'20px 20px 0 0', width:'100%', maxWidth:500, overflow:'hidden' }}>
            <div style={{ width:36,height:4,background:'var(--border)',borderRadius:2,margin:'10px auto 0' }}/>
            <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 18px',borderBottom:'1px solid var(--border)' }}>
              <span style={{ fontWeight:700 }}>Artikel hinzufügen</span>
              <button onClick={() => setAddModal(false)} style={{ display:'flex',alignItems:'center',padding:7,border:'none',background:'var(--bg)',cursor:'pointer',borderRadius:8,color:'var(--text)' }}><X size={16}/></button>
            </div>
            <div style={{ padding:'16px 18px', display:'flex', flexDirection:'column', gap:12 }}>
              <div className="f-group">
                <label className="f-label">Name *</label>
                <input
                  className="f-input"
                  value={newItem.name}
                  onChange={e => setNewItem(p=>({...p,name:e.target.value}))}
                  placeholder="z.B. Biergläser"
                  autoFocus
                />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="f-group">
                  <label className="f-label">Menge</label>
                  <input type="number" className="f-input" min={0.1} step={0.1}
                    value={newItem.quantity}
                    onChange={e => setNewItem(p=>({...p,quantity:e.target.value}))}/>
                </div>
                <div className="f-group">
                  <label className="f-label">Einheit</label>
                  <select className="f-input" value={newItem.unit} onChange={e=>setNewItem(p=>({...p,unit:e.target.value}))}>
                    {UNITS.map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div className="f-group">
                <label className="f-label">Kategorie</label>
                <select className="f-input" value={newItem.category} onChange={e=>setNewItem(p=>({...p,category:e.target.value}))}>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="f-group">
                <label className="f-label">Notiz</label>
                <input className="f-input" value={newItem.note} onChange={e=>setNewItem(p=>({...p,note:e.target.value}))} placeholder="Optional"/>
              </div>
            </div>
            <div style={{ padding:'14px 18px', borderTop:'1px solid var(--border)', display:'flex', gap:10 }}>
              <button className="btn btn-secondary" style={{ flex:1, justifyContent:'center' }} onClick={() => setAddModal(false)}>Abbrechen</button>
              <button className="btn btn-primary" style={{ flex:1, justifyContent:'center' }} onClick={addItem} disabled={addingSave}>
                {addingSave ? <><span className="spinner" style={{width:14,height:14,borderWidth:2}}/> Hinzufügen...</> : <><Plus size={14}/> Hinzufügen</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Items nach Kategorie */}
      {Object.keys(items).length === 0 ? (
        <div className="card">
          <div className="empty-box">
            <Package size={36} style={{ opacity:.3 }}/>
            <h3>Noch keine Artikel</h3>
            <p style={{ fontSize:'.82rem', color:'var(--text-muted)' }}>Füge Artikel über den Button oben hinzu.</p>
            <button className="btn btn-primary" style={{ marginTop:10 }} onClick={() => { setNewItem(EMPTY_ITEM); setAddModal(true); }}>
              <Plus size={14}/> Artikel hinzufügen
            </button>
          </div>
        </div>
      ) : (
        Object.entries(items).map(([category, catItems]) => {
          const catPacked = catItems.filter(i => i.packed || i.status==='gepackt').length;
          const isCollapsed = collapsed[category];
          return (
            <div key={category} className="card" style={{ marginBottom:10 }}>
              <button
                onClick={() => setCollapsed(p=>({...p,[category]:!p[category]}))}
                style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',width:'100%',background:'var(--bg)',border:'none',cursor:'pointer',fontFamily:'inherit',textAlign:'left',borderBottom:isCollapsed?'none':'1px solid var(--border)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontWeight:700, fontSize:'.9rem' }}>{category}</span>
                  <span style={{
                    padding:'2px 8px', borderRadius:999, fontSize:'.68rem', fontWeight:700,
                    background: catPacked===catItems.length ? 'var(--green-bg)' : 'var(--border)',
                    color: catPacked===catItems.length ? 'var(--green)' : 'var(--text-muted)',
                  }}>
                    {catPacked}/{catItems.length}
                  </span>
                </div>
                {isCollapsed ? <ChevronDown size={16} color="var(--text-muted)"/> : <ChevronUp size={16} color="var(--text-muted)"/>}
              </button>

              {!isCollapsed && catItems.map((item, idx) => {
                const isPacked = item.packed || item.status === 'gepackt';
                const isEditing = editItem?.id === item.id;
                return (
                  <div key={item.id}>
                    {isEditing ? (
                      // Inline-Bearbeitung
                      <div style={{ padding:'12px 16px', background:'var(--bg)', borderBottom: idx<catItems.length-1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 80px 80px', gap:8, marginBottom:8 }}>
                          <input className="f-input" value={editItemForm.name||''} onChange={e=>setEditItemForm(p=>({...p,name:e.target.value}))} placeholder="Name"/>
                          <input type="number" className="f-input" value={editItemForm.quantity||1} onChange={e=>setEditItemForm(p=>({...p,quantity:e.target.value}))}/>
                          <select className="f-input" value={editItemForm.unit||'Stück'} onChange={e=>setEditItemForm(p=>({...p,unit:e.target.value}))}>
                            {UNITS.map(u=><option key={u}>{u}</option>)}
                          </select>
                        </div>
                        <select className="f-input" style={{ marginBottom:8 }} value={editItemForm.category||'Sonstiges'} onChange={e=>setEditItemForm(p=>({...p,category:e.target.value}))}>
                          {CATEGORIES.map(c=><option key={c}>{c}</option>)}
                        </select>
                        <div style={{ display:'flex', gap:8 }}>
                          <button className="btn btn-primary btn-sm" onClick={saveEditItem}><Save size={12}/> Speichern</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditItem(null)}><X size={12}/> Abbrechen</button>
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          display:'flex', alignItems:'center', gap:12,
                          padding:'12px 16px',
                          borderBottom: idx<catItems.length-1 ? '1px solid var(--border)' : 'none',
                          background: isPacked ? 'var(--green-bg)' : 'var(--bg-card)',
                          transition:'background 200ms', cursor:'pointer',
                        }}
                        onClick={() => toggleItem(item)}>
                        {/* Checkbox */}
                        <div style={{
                          width:26, height:26, borderRadius:7, border:'2px solid', flexShrink:0,
                          display:'flex', alignItems:'center', justifyContent:'center',
                          background: isPacked ? 'var(--green)' : 'transparent',
                          borderColor: isPacked ? 'var(--green)' : 'var(--border)',
                          transition:'all 150ms',
                        }}>
                          {isPacked && <Check size={14} color="#fff" strokeWidth={3}/>}
                        </div>

                        {/* Info */}
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{
                            fontWeight:600, fontSize:'.88rem',
                            color: isPacked ? 'var(--text-muted)' : 'var(--text)',
                            textDecoration: isPacked ? 'line-through' : 'none',
                            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                          }}>
                            {item.name}
                          </div>
                          {(item.quantity || item.unit) && (
                            <div style={{ fontSize:'.72rem', color:'var(--text-muted)', marginTop:1 }}>
                              {item.quantity} {item.unit}
                            </div>
                          )}
                          {item.note && (
                            <div style={{ fontSize:'.72rem', color:'var(--text-muted)', fontStyle:'italic', marginTop:1 }}>
                              {item.note}
                            </div>
                          )}
                        </div>

                        {/* Aktionen */}
                        <div style={{ display:'flex', gap:4, flexShrink:0 }} onClick={e => e.stopPropagation()}>
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => { setEditItem(item); setEditItemForm({name:item.name,quantity:item.quantity,unit:item.unit,category:item.category,note:item.note}); }}>
                            <Edit2 size={12}/>
                          </button>
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            style={{ color:'var(--red)' }}
                            onClick={e => deleteItem(item, e)}>
                            <Trash2 size={12}/>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })
      )}
    </Layout>
  );
}
